import * as React from 'react';

import {
  Switch as AriaSwitch,
  SwitchProps as AriaSwitchProps,
  SwitchRenderProps,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { Row } from '../containers';

const Switch = ({
  children,
  className,
  onChange,
  isSelected: isSelectedProp,
  ...props
}: AriaSwitchProps) => {
  const [isSelected, setIsSelected] = React.useState(isSelectedProp);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    setIsSelected(isSelectedProp);
  }, [isSelectedProp]);

  const handleChange = (isSelected: boolean) => {
    setIsSelected(isSelected);

    // Clear any existing timeout to avoid multiple pending callbacks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // let the animation finish before calling the onChange callback
    timeoutRef.current = setTimeout(() => onChange?.(isSelected), 200);
  };

  React.useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const cssVariables = ['[--padding:3px]', '[--size:15px]'];

  return (
    <AriaSwitch
      className={cn('@Switch', className)}
      onChange={handleChange}
      isSelected={isSelected}
      {...props}
    >
      {composeRenderProps(
        children,
        (
          children,
          props: SwitchRenderProps & {
            defaultChildren: React.ReactNode | undefined;
          },
        ) => (
          <Row className={'gap-1.5'}>
            <div
              className={cn(
                ...cssVariables,
                'w-[calc(var(--size)*2.5)] rounded-full bg-neutral/40 p-[var(--padding)] transition-all duration-200',
                props.isSelected && 'bg-primary',
                props.isFocusVisible && 'ring-2',
                props.isFocusVisible && props.isSelected && 'ring-primary/40',
                props.isFocusVisible && !props.isSelected && 'ring-neutral/20',
                props.isDisabled && 'bg-neutral/10',
              )}
            >
              <div
                className={cn(
                  ...cssVariables,
                  'size-(--size) rounded-full bg-abs-white transition-all duration-200',
                  props.isSelected &&
                    'translate-x-[calc(var(--size)*2.5-var(--size)-var(--padding)*2)]',
                  props.isDisabled && 'opacity-50',
                )}
              />
            </div>
            <span className="contents text-sm text-neutral/70">{children}</span>
          </Row>
        ),
      )}
    </AriaSwitch>
  );
};

export { Switch };
